import { useAuth } from "@/core/auth/hooks/useAuth";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MessageSendIntentSchema,
  type CreateMessageRequest,
  type MessageSendIntent,
} from "../types/messageTypes";

interface Draft {
  text: string;
  pending: boolean;
  intent?: MessageSendIntent;
  error?: string;
  blocked?: boolean;
}

const unresolvedMessage = "Sending is not confirmed. The original message may already have been sent. Editing and new sends are paused. Check / retry uses the same message and key, never a second delivery. Do not resend from another tab or device.";

function restoreDraft(storageKey: string): Draft {
  try {
    const saved = sessionStorage.getItem(storageKey);
    if (!saved) return { text: "", pending: false };
    const intent = MessageSendIntentSchema.parse(JSON.parse(saved));
    return { text: intent.request.body, pending: false, intent, error: unresolvedMessage };
  } catch {
    return {
      text: "", pending: false, blocked: true,
      error: "Could not recover the previous send safely. New sends are paused. Contact support to confirm delivery before sending again.",
    };
  }
}

export function useMessageDraft(businessId?: string, conversationId?: string) {
  const { user, getAuthGeneration } = useAuth();
  const queryClient = useQueryClient();
  const key = ["messageDraft", user?.id, businessId, conversationId] as const;
  const storageKey = JSON.stringify(key);
  const { data: draft } = useQuery<Draft>({
    queryKey: key,
    queryFn: () => restoreDraft(storageKey),
    initialData: () => restoreDraft(storageKey),
    enabled: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const setText = (text: string) => {
    queryClient.setQueryData<Draft>(key, (old) => old && !old.intent && !old.pending && !old.blocked
      ? { ...old, text, error: undefined }
      : old);
  };

  const send = async (
    request: CreateMessageRequest,
    submit: (intent: MessageSendIntent) => Promise<void>,
  ) => {
    const current = queryClient.getQueryData<Draft>(key);
    if (!user || !businessId || !conversationId || !current || current.blocked
      || current.pending || !current.text.trim()) return;
    const generation = getAuthGeneration();
    // The complete payload, not just its body, belongs to the intent across retries and reloads.
    const intent = current.intent ?? MessageSendIntentSchema.parse({
      idempotencyKey: crypto.randomUUID(),
      request,
    });
    if (intent.request.businessId !== businessId || intent.request.conversationId !== conversationId) return;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(intent));
    } catch {
      queryClient.setQueryData<Draft>(key, {
        ...current, error: "Sending is paused because this browser cannot save a safe retry. Enable session storage and try again.",
      });
      return;
    }
    queryClient.setQueryData<Draft>(key, { ...current, intent, pending: true, error: undefined });
    try {
      await submit(intent);
      if (generation !== getAuthGeneration()) return;
      sessionStorage.removeItem(storageKey);
      queryClient.setQueryData<Draft>(key, (latest) => latest?.intent?.idempotencyKey === intent.idempotencyKey
        ? { text: "", pending: false }
        : latest);
    } catch {
      if (generation !== getAuthGeneration()) return;
      queryClient.setQueryData<Draft>(key, (latest) => latest?.intent?.idempotencyKey === intent.idempotencyKey
        ? { ...latest, pending: false, error: unresolvedMessage }
        : latest);
    }
  };

  return { draft, setText, send };
}
